// OrbitCameraRig.tsx — Shared camera rig for all 3D viewers.
// Single source of truth for orbit/pan/zoom navigation.
// Spherical-coordinate orbit with zero drift (no OrbitControls library).
//
// Navigation:
//   Left drag         — orbit
//   Right / mid drag  — pan (Google Earth style)
//   Scroll wheel      — cursor-anchored zoom
//   Two-finger pinch  — zoom + pan centroid
//
// Parent responsibilities:
//   • Mount in a <Canvas> subtree (uses useThree/useFrame)
//   • Keep centerRef / sizeRef current when scene content changes
//   • Trigger preset changes via the preset + resetKey props
//   • Wire up zoom/pan button callbacks via zoomRef / panRef
//   • Handle keyboard shortcuts on the outer div (keydown)

import { useRef, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { ViewPreset } from '../../types/panelvar';

// ── Preset direction vectors ──────────────────────────────────────────────────
export const PRESET_DIR: Record<ViewPreset, [number, number, number]> = {
  'front':   [0,     0,      1],
  'back':    [0,     0,     -1],
  'left':    [-1,    0,      0],
  'right':   [1,     0,      0],
  'top':     [0,     1,      0.001],
  'bottom':  [0,    -1,      0.001],
  'br-iso':  [1,     0.67,  -1],
  'bl-iso':  [-1,    0.67,  -1],
  'fr-iso':  [1,     0.67,   1],
  'fl-iso':  [-1,    0.67,   1],
};

// ── Helpers (exported so callers can use the same math) ───────────────────────
export function sphericalToCartesian(theta: number, phi: number, dist: number): [number, number, number] {
  const sp = Math.sin(phi);
  return [
    dist * sp * Math.sin(theta),
    dist * Math.cos(phi),
    dist * sp * Math.cos(theta),
  ];
}

export function fitDistance(viewW: number, viewH: number, fovDeg = 45, aspect = 1): number {
  const halfTan = Math.tan((fovDeg * Math.PI / 180) / 2);
  return Math.max((viewH / 2) / halfTan, (viewW / 2) / (aspect * halfTan)) * 1.5;
}

export function projectedSize(
  dir: [number, number, number],
  sx: number,
  sy: number,
  sz: number,
): { w: number; h: number } {
  const [dx, dy, dz] = dir;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
  const ay = Math.abs(dy / len);
  const maxDim = Math.max(sx, sz);
  if (ay > 0.5) return { w: maxDim, h: maxDim };
  return { w: maxDim, h: sy };
}

function findPanelId(obj: THREE.Object3D): string | null {
  let cur: THREE.Object3D | null = obj;
  while (cur) {
    if (cur.userData?.panelId) return cur.userData.panelId as string;
    cur = cur.parent;
  }
  return null;
}

// ── Camera state ──────────────────────────────────────────────────────────────
interface CamState {
  theta: number;
  phi: number;
  dist: number;
  near: number;
  far: number;
  tx: number;
  ty: number;
  tz: number;
}

// ── Props ─────────────────────────────────────────────────────────────────────
export interface OrbitCameraRigProps {
  /** Scene bounding center — look-at target. Keep current; no re-render needed. */
  centerRef: React.MutableRefObject<{ x: number; y: number; z: number }>;
  /** Scene bounding size (sx, sy, sz). Used for preset fit-to-view. */
  sizeRef: React.MutableRefObject<{ sx: number; sy: number; sz: number }>;
  /** Active view preset. Changing triggers a camera jump to that view. */
  preset: ViewPreset | null;
  /** Stable identity of the content being viewed (e.g. panel ID, segment key).
   *  Changes force the preset view to re-apply even if preset value is unchanged. */
  contentKey?: string | number;
  /** Increment to force a camera reset to the current preset. */
  resetKey?: number;
  /** Initial polar angle (radians). π/2 = level, 0.4 = mild top-down tilt. */
  initialPhi?: number;
  /** Exposes zoomBy(factor) to parent — wire to toolbar +/− buttons. */
  zoomRef?: React.MutableRefObject<((factor: number) => void) | null>;
  /** Exposes panBy(dx, dy) to parent — wire to toolbar arrow buttons. */
  panRef?: React.MutableRefObject<((dx: number, dy: number) => void) | null>;
  /** Exposes fitView() to parent — fits content at current camera angle (no rotation). */
  fitViewRef?: React.MutableRefObject<(() => void) | null>;
  /** Called when user manually moves camera (use to clear the active preset). */
  onManualMove?: () => void;
  /** When true, suppresses orbit/pan drag start (set by panel drag handlers). */
  disabledRef?: React.MutableRefObject<boolean>;
  /** Optional: raycasts userData.panelId on left click. */
  onPanelClick?: (panelId: string, ctrlKey: boolean) => void;
  /** Optional: fires after two left clicks on the same panel within 400 ms. */
  onPanelDoubleClick?: (panelId: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function OrbitCameraRig({
  centerRef,
  sizeRef,
  preset,
  contentKey,
  resetKey = 0,
  initialPhi = Math.PI / 2,
  zoomRef,
  panRef,
  fitViewRef,
  onManualMove,
  disabledRef,
  onPanelClick,
  onPanelDoubleClick,
}: OrbitCameraRigProps) {
  const { camera, gl, invalidate, scene } = useThree();

  const state = useRef<CamState>({
    theta: 0, phi: initialPhi, dist: 500,
    near: 0.1, far: 1_000_000,
    tx: 0, ty: 0, tz: 0,
  });
  const dragging      = useRef(false);
  const dragMode      = useRef<'none' | 'orbit' | 'pan'>('none');
  const lastPointer   = useRef({ x: 0, y: 0 });
  const downPointer   = useRef<{ x: number; y: number; ctrlKey: boolean } | null>(null);
  const lastClickTime = useRef(0);
  const lastClickId   = useRef<string | null>(null);
  const raycaster     = useRef(new THREE.Raycaster());

  // ── Expose zoom callback to parent ─────────────────────────────────────────
  useEffect(() => {
    if (!zoomRef) return;
    zoomRef.current = (factor: number) => {
      state.current.dist = Math.max(1, Math.min(1_000_000, state.current.dist * factor));
      invalidate();
    };
    return () => { if (zoomRef) zoomRef.current = null; };
  }, [zoomRef, invalidate]);

  // ── Expose pan callback to parent ──────────────────────────────────────────
  useEffect(() => {
    if (!panRef) return;
    panRef.current = (dx: number, dy: number) => {
      const s = state.current;
      const ps = s.dist * 0.002;
      const rx = Math.cos(s.theta), rz = -Math.sin(s.theta);
      const cp = Math.cos(s.phi), sp = Math.sin(s.phi);
      const ux = -cp * Math.sin(s.theta), uy = sp, uz = -cp * Math.cos(s.theta);
      s.tx += (-dx * rx + dy * ux) * ps;
      s.ty += dy * uy * ps;
      s.tz += (-dx * rz + dy * uz) * ps;
      invalidate();
    };
    return () => { if (panRef) panRef.current = null; };
  }, [panRef, invalidate]);

  // ── Expose fitView callback to parent ──────────────────────────────────────
  useEffect(() => {
    if (!fitViewRef) return;
    fitViewRef.current = () => {
      const { sx, sy, sz } = sizeRef.current;
      const s = state.current;
      const [dx, dy, dz] = sphericalToCartesian(s.theta, s.phi, 1);
      const dir: [number, number, number] = [dx, dy, dz];
      const { w, h } = projectedSize(dir, sx, sy, sz);
      const cam = camera as THREE.PerspectiveCamera;
      const el = gl.domElement;
      const aspect = el.clientWidth > 0 && el.clientHeight > 0
        ? el.clientWidth / el.clientHeight
        : (cam.aspect > 0 ? cam.aspect : 1);
      const dist = fitDistance(w, h, cam.fov ?? 45, aspect);
      if (!Number.isFinite(dist) || dist <= 0) return;
      const c = centerRef.current;
      s.dist = dist;
      s.tx = c.x; s.ty = c.y; s.tz = c.z;
      s.near = dist * 0.001;
      s.far  = dist * 50;
      cam.near = s.near;
      cam.far  = s.far;
      cam.updateProjectionMatrix();
      invalidate();
    };
    return () => { if (fitViewRef) fitViewRef.current = null; };
  }, [fitViewRef, sizeRef, centerRef, camera, gl, invalidate]);

  // ── Apply preset view ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!preset) return;
    const { sx, sy, sz } = sizeRef.current;
    if (!sx && !sy && !sz) return;
    const cam = camera as THREE.PerspectiveCamera;
    const el  = gl.domElement;
    const aspect = el.clientWidth > 0 && el.clientHeight > 0
      ? el.clientWidth / el.clientHeight
      : (cam.aspect > 0 ? cam.aspect : 1);
    const dir  = PRESET_DIR[preset];
    const { w, h } = projectedSize(dir, sx, sy, sz);
    const dist = fitDistance(w, h, cam.fov ?? 45, aspect);
    if (!Number.isFinite(dist) || dist <= 0) return;
    const [dx, dy, dz] = dir;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
    const theta = Math.atan2(dx / len, dz / len);
    const phi   = Math.acos(Math.max(-1, Math.min(1, dy / len)));
    const c = centerRef.current;
    state.current = {
      theta,
      phi: Math.max(0.01, Math.min(Math.PI - 0.01, phi)),
      dist,
      near: dist * 0.001,
      far:  dist * 50,
      tx: c.x, ty: c.y, tz: c.z,
    };
    const [px, py, pz] = sphericalToCartesian(theta, phi, dist);
    camera.position.set(px + c.x, py + c.y, pz + c.z);
    camera.lookAt(c.x, c.y, c.z);
    cam.near = state.current.near;
    cam.far  = state.current.far;
    cam.updateProjectionMatrix();
    invalidate();
  }, [preset, contentKey, resetKey, camera, gl, sizeRef, centerRef, invalidate]);

  // ── Enforce camera position every frame (zero drift) ───────────────────────
  useFrame(() => {
    const s = state.current;
    const [ox, oy, oz] = sphericalToCartesian(s.theta, s.phi, s.dist);
    camera.position.set(ox + s.tx, oy + s.ty, oz + s.tz);
    camera.lookAt(s.tx, s.ty, s.tz);
  });

  // ── Pointer + wheel event handlers ─────────────────────────────────────────
  useEffect(() => {
    const el  = gl.domElement;
    const inv = invalidate;
    const ZOOM     = 1.15;
    const CLICK_PX = 4;

    const pinchPts = new Map<number, { x: number; y: number }>();
    let prevPinch: { dist: number; cx: number; cy: number } | null = null;

    function getPinch() {
      const pts = [...pinchPts.values()];
      if (pts.length < 2) return null;
      const [a, b] = pts;
      return { dist: Math.hypot(b.x - a.x, b.y - a.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
    }

    function doCursorZoom(clientX: number, clientY: number, factor: number) {
      const s = state.current;
      const newDist = Math.max(1, Math.min(1_000_000, s.dist * factor));
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const ndcX = ((clientX - rect.left)  / rect.width)  * 2 - 1;
        const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
        const [cx, cy, cz] = sphericalToCartesian(s.theta, s.phi, 1);
        const fwd   = new THREE.Vector3(-cx, -cy, -cz).normalize();
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(fwd, new THREE.Vector3(s.tx, s.ty, s.tz));
        const hit   = new THREE.Vector3();
        if (raycaster.current.ray.intersectPlane(plane, hit)) {
          const t = (1 - newDist / s.dist) * 0.5;
          s.tx += (hit.x - s.tx) * t;
          s.ty += (hit.y - s.ty) * t;
          s.tz += (hit.z - s.tz) * t;
        }
      }
      s.dist = newDist;
    }

    function hitsDraggable(clientX: number, clientY: number): boolean {
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;
      const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
      const hits = raycaster.current.intersectObjects(scene.children, true);
      for (const hit of hits) {
        let obj: THREE.Object3D | null = hit.object;
        while (obj) { if (obj.userData?.draggable) return true; obj = obj.parent; }
      }
      return false;
    }

    const onPointerDown = (e: PointerEvent) => {
      if (disabledRef?.current) return;
      if (e.pointerType !== 'mouse') {
        pinchPts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        el.setPointerCapture(e.pointerId);
        if (pinchPts.size >= 2) {
          prevPinch = getPinch();
          dragging.current = false; dragMode.current = 'none'; downPointer.current = null;
        }
        return;
      }
      if (e.button === 0) {
        // Skip orbit start if the click landed on a draggable panel
        if (disabledRef && hitsDraggable(e.clientX, e.clientY)) {
          disabledRef.current = true;
          return;
        }
        downPointer.current = { x: e.clientX, y: e.clientY, ctrlKey: e.ctrlKey || e.metaKey };
        lastPointer.current = { x: e.clientX, y: e.clientY };
        el.setPointerCapture(e.pointerId);
      }
      if (e.button === 1) {
        e.preventDefault();
        dragging.current = true; dragMode.current = 'pan';
        lastPointer.current = { x: e.clientX, y: e.clientY };
        el.setPointerCapture(e.pointerId);
      }
      if (e.button === 2) {
        dragging.current = true; dragMode.current = 'pan';
        lastPointer.current = { x: e.clientX, y: e.clientY };
        el.setPointerCapture(e.pointerId);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' && pinchPts.has(e.pointerId)) {
        pinchPts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pinchPts.size >= 2) {
          const pinch = getPinch();
          if (pinch && prevPinch) {
            if (prevPinch.dist > 1) doCursorZoom(pinch.cx, pinch.cy, prevPinch.dist / pinch.dist);
            const pdx = pinch.cx - prevPinch.cx, pdy = pinch.cy - prevPinch.cy;
            if (pdx || pdy) {
              const s = state.current;
              const ps = s.dist * 0.002;
              const rx = Math.cos(s.theta), rz = -Math.sin(s.theta);
              const cp = Math.cos(s.phi), sp = Math.sin(s.phi);
              const ux = -cp * Math.sin(s.theta), uy = sp, uz = -cp * Math.cos(s.theta);
              s.tx += (-pdx * rx + pdy * ux) * ps;
              s.ty += pdy * uy * ps;
              s.tz += (-pdx * rz + pdy * uz) * ps;
            }
            onManualMove?.(); inv();
          }
          prevPinch = pinch;
          return;
        }
      }

      if (downPointer.current && !dragging.current) {
        const adx = Math.abs(e.clientX - downPointer.current.x);
        const ady = Math.abs(e.clientY - downPointer.current.y);
        if (adx >= CLICK_PX || ady >= CLICK_PX) {
          dragging.current = true; dragMode.current = 'orbit';
          lastPointer.current = { x: downPointer.current.x, y: downPointer.current.y };
          downPointer.current = null;
        }
        if (!dragging.current) return;
      }

      if (!dragging.current) return;
      const mdx = e.clientX - lastPointer.current.x;
      const mdy = e.clientY - lastPointer.current.y;
      lastPointer.current = { x: e.clientX, y: e.clientY };
      const s = state.current;

      if (dragMode.current === 'orbit') {
        s.theta -= mdx * 0.005;
        s.phi    = Math.max(0.01, Math.min(Math.PI - 0.01, s.phi + mdy * 0.005));
        onManualMove?.();
      } else if (dragMode.current === 'pan') {
        const ps = s.dist * 0.002;
        const rx = Math.cos(s.theta), rz = -Math.sin(s.theta);
        const cp = Math.cos(s.phi), sp = Math.sin(s.phi);
        const ux = -cp * Math.sin(s.theta), uy = sp, uz = -cp * Math.cos(s.theta);
        s.tx += (-mdx * rx + mdy * ux) * ps;
        s.ty += mdy * uy * ps;
        s.tz += (-mdx * rz + mdy * uz) * ps;
        onManualMove?.();
      }
      inv();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') {
        pinchPts.delete(e.pointerId);
        if (pinchPts.size < 2) prevPinch = null;
      }
      const dp         = downPointer.current;
      const wasDragging = dragging.current;
      dragging.current = false; dragMode.current = 'none'; downPointer.current = null;
      try { el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }

      if (e.button === 0 && !wasDragging && dp && onPanelClick) {
        const adx = Math.abs(e.clientX - dp.x), ady = Math.abs(e.clientY - dp.y);
        if (adx <= CLICK_PX && ady <= CLICK_PX) {
          const rect = el.getBoundingClientRect();
          const ndcX = ((e.clientX - rect.left)  / rect.width)  * 2 - 1;
          const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
          raycaster.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
          const hits = raycaster.current.intersectObjects(scene.children, true);
          let hitId: string | null = null;
          for (const hit of hits) { hitId = findPanelId(hit.object); if (hitId) break; }
          if (hitId) {
            const now = Date.now();
            if (now - lastClickTime.current < 400 && lastClickId.current === hitId) {
              onPanelDoubleClick?.(hitId);
              lastClickTime.current = 0; lastClickId.current = null;
            } else {
              onPanelClick(hitId, dp.ctrlKey);
              lastClickTime.current = now; lastClickId.current = hitId;
            }
          }
        }
      }
    };

    const onWheel = (e: WheelEvent) => {
      doCursorZoom(e.clientX, e.clientY, e.deltaY > 0 ? ZOOM : (1 / ZOOM));
      onManualMove?.(); inv();
    };

    const onContextMenu = (e: Event) => e.preventDefault();
    const onAuxClick    = (e: MouseEvent) => { if (e.button === 1) e.preventDefault(); };

    el.addEventListener('pointerdown',   onPointerDown);
    el.addEventListener('pointermove',   onPointerMove);
    el.addEventListener('pointerup',     onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    el.addEventListener('wheel',         onWheel, { passive: true });
    el.addEventListener('contextmenu',   onContextMenu);
    el.addEventListener('auxclick',      onAuxClick);

    return () => {
      el.removeEventListener('pointerdown',   onPointerDown);
      el.removeEventListener('pointermove',   onPointerMove);
      el.removeEventListener('pointerup',     onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('wheel',         onWheel);
      el.removeEventListener('contextmenu',   onContextMenu);
      el.removeEventListener('auxclick',      onAuxClick);
    };
  }, [gl, invalidate, onManualMove, disabledRef, camera, scene, onPanelClick, onPanelDoubleClick]);

  return null;
}
